
export const gridListData = {
    itemPriceGrid: {
        entity: 'itemPrice',
        id: 'itemPrice',
        linesArray: 'itemPrices',
        listHeader: {
            text: 'product.price.grid.title',
        },
        meta: { width: '1024px' },
        columns: [
            { id: 'code', label: 'product.price.grid.code', type: 'select', width: '150px', editable: true, entity: 'priceType', displayField: 'codeName', valueMap: { code: 'id', codeName: 'name' } },
            { id: 'typeName', label: 'product.price.grid.type', type: 'text', width: '120px', editable: false },
            { id: 'value', label: 'product.price.grid.value', type: 'number', width: '100px', editable: true, decimals: 2 },
            { id: 'netSalesPrice', label: 'product.price.grid.price', type: 'amount', width: '120px', editable: false },
        ],
        mobile: {
            left: {
                tag: { field: 'codeName' }
            },
            columns: [
                { id: 'codeName', label: 'product.price.grid.code' },
                { id: 'typeName', label: 'product.price.grid.type' },
                { id: 'value', label: 'product.price.grid.value', type: 'number', editable: true, decimals: 2 },
                { id: 'netSalesPrice', label: 'product.price.grid.price' },
            ]
        },
        orderBy: 'code',
        reorderable: true,
        addLine: 'product.price.grid.new',
        defaultData: [
            { id: 'exchangeRate', defaultType: 'value', defaultValue: 1 },
        ],
        fieldsLogic: ['value', 'typeId'],
        buttons: [
            { id: 'itemPriceGrid.save', action: 'save', label: 'all.grid.save', icon: 'save', variant: 'primary' },
        ],
        lineActions: {
            menuItems: [
                { action: 'delete', label: 'product.price.grid.delete', confirm: true, modalConfig: 'confirmDelete' },
            ],
        }
    },
    /*
     * fastJournalCard's lines: the journal's own `lines` array, edited in place
     * and saved with the journal. Every edit reruns the journal chain
     * (logicData), so the header totals follow as amounts are typed.
     *
     * A grid has one shape for every row, so the conditional fields of
     * journalCard's lines are not here: VAT is always offered (every code,
     * sales and purchases), and the party, the document and a deductible share
     * are entered in journalCard.
     */
    journalLinesGrid: {
        entity: 'journal',
        linesArray: 'lines',
        listHeader: { text: 'journal.fast.grid.title' },
        columns: [
            {
                id: 'linePreset',
                label: 'journal.form.line.type',
                type: 'select',
                width: '180px',
                editable: true,
                entity: 'journalLinePreset',
                displayField: 'typeName',
                valueMap: {
                    'linePreset': 'id',
                    'type': 'type',
                    'typeName': 'name',
                    'typeRefType': 'typeRefType',
                    'typeRefId': 'typeRefId',
                    'typeRefIdName': 'typeRefIdName',
                    'documentType': 'documentType',
                    'documentId': 'documentId',
                    'documentNumber': 'documentNumber'
                },
                // A type carries no party or document, so picking one clears
                // the line's: a customer picked under "payment received" must
                // not stay on the line once it says "payment made".
                nullDefaults: {
                    typeRefType: null,
                    typeRefId: null,
                    typeRefIdName: '',
                    documentType: null,
                    documentId: null,
                    documentNumber: null
                },
                options: { withHeaders: false, autoSelectFirst: false }
            },
            {
                id: 'accountId',
                label: 'journal.form.line.account',
                type: 'connect',
                width: '240px',
                editable: true,
                entity: 'glAccount',
                displayField: 'accountIdName',
                valueMap: {
                    'accountId': 'id',
                    'accountIdName': 'name',
                    'accountNo': 'number',
                    'accountNormalBalance': 'normalBalance',
                    'accountCategory': 'category'
                },
                modalTitle: 'journal.form.line.account.modal.title',
                listDisplay: { text: '${2} - ${1}', params: ['name', 'number'] },
                inline: true,
                // Bold exactly the rows that cannot be picked: the headings
                // and sums that give the chart its shape.
                options: {
                    withHeaders: false,
                    highlight: { condition: { field: 'isPostable', operator: 'equals', value: false } },
                    selectable: { condition: { field: 'isPostable', operator: 'notEquals', value: false } }
                }
            },
            {
                id: 'description',
                label: 'journal.form.line.description',
                type: 'text',
                width: '200px',
                editable: true
            },
            {
                id: 'debitAmount',
                label: 'journal.form.line.debit',
                type: 'amount',
                width: '120px',
                editable: true,
                decimals: 2
            },
            {
                id: 'creditAmount',
                label: 'journal.form.line.credit',
                type: 'amount',
                width: '120px',
                editable: true,
                decimals: 2
            },
            {
                id: 'counterAccountId',
                label: 'journal.form.line.counterAccount',
                type: 'connect',
                width: '240px',
                editable: true,
                entity: 'glAccount',
                displayField: 'counterAccountIdName',
                valueMap: {
                    'counterAccountId': 'id',
                    'counterAccountIdName': 'name',
                    'counterAccountCategory': 'category'
                },
                modalTitle: 'journal.form.line.counterAccount.modal.title',
                listDisplay: { text: '${2} - ${1}', params: ['name', 'number'] },
                inline: true,
                options: {
                    withHeaders: false,
                    highlight: { condition: { field: 'isPostable', operator: 'equals', value: false } },
                    selectable: { condition: { field: 'isPostable', operator: 'notEquals', value: false } }
                }
            },
            {
                id: 'vatTaxId',
                label: 'journal.form.line.vatTax',
                type: 'select',
                width: '150px',
                editable: true,
                entity: 'vatTax',
                displayField: 'vatTaxIdName',
                valueMap: {
                    'vatTaxId': 'id',
                    'vatTaxIdName': 'name',
                    'vatTaxJurisdiction': 'jurisdiction',
                    'vatTaxDeductionMethod': 'deductionMethod'
                },
                options: { withHeaders: false, autoSelectFirst: false, showSubtitle: false }
            },
            /*
             * The party and the document, at the end so every other column
             * keeps its place. What they search follows the line's type: a
             * customer and their open invoices, or a supplier and their open
             * expenses. A type against no one greys both out, and Tab passes
             * them by. A new party clears the document; picking the document
             * first fills in its party.
             */
            {
                id: 'typeRefId',
                label: 'journal.fast.grid.party',
                type: 'connect',
                width: '170px',
                editable: true,
                entity: 'contact',
                entityParams: { type: 'filter', filters: [{ key: 'contactType', value: 'customer|both|undefined' }] },
                displayField: 'typeRefIdName',
                valueMap: {
                    'typeRefId': 'id',
                    'typeRefIdName': 'name',
                    'documentType': 'documentType',
                    'documentId': 'documentId',
                    'documentNumber': 'documentNumber'
                },
                nullDefaults: {
                    documentType: null,
                    documentId: null,
                    documentNumber: null
                },
                modalTitle: 'journal.form.line.customer.modal.title',
                inline: true,
                options: { withHeaders: false },
                conditionals: [
                    {
                        condition: { field: 'typeRefType', operator: 'equals', value: 'supplier' },
                        set: {
                            entityParams: { type: 'filter', filters: [{ key: 'contactType', value: 'supplier|both' }] },
                            modalTitle: 'journal.form.line.vendor.modal.title'
                        }
                    },
                    {
                        condition: { field: 'typeRefType', operator: 'notIn', value: ['customer', 'supplier'] },
                        set: { editable: false }
                    }
                ]
            },
            {
                id: 'documentId',
                label: 'journal.fast.grid.document',
                type: 'connect',
                width: '110px',
                editable: true,
                entity: 'invoice',
                entityParams: {
                    type: 'filter',
                    filters: [
                        { key: 'customerId', value: '${typeRefId}' },
                        { key: 'state', value: 'final' },
                        { key: 'paid', value: 'false|undefined' }
                    ]
                },
                displayField: 'documentNumber',
                valueMap: {
                    'documentId': 'id',
                    'documentNumber': 'bookedNumber',
                    'documentType': 'entity',
                    'typeRefId': 'customerId',
                    'typeRefIdName': 'customerName'
                },
                modalTitle: 'journal.form.line.invoice.modal.title',
                menuItemField: 'bookedNumber',
                inline: true,
                // The last column: its list opens leftwards, inside the card.
                options: { withHeaders: false, dropdownAlign: 'right' },
                conditionals: [
                    {
                        condition: { field: 'typeRefType', operator: 'equals', value: 'supplier' },
                        set: {
                            entity: 'expense',
                            entityParams: {
                                type: 'filter',
                                filters: [
                                    { key: 'supplierId', value: '${typeRefId}' },
                                    { key: 'state', value: 'final' },
                                    { key: 'paid', value: 'false|undefined' }
                                ]
                            },
                            valueMap: {
                                'documentId': 'id',
                                'documentNumber': 'bookedNumber',
                                'documentType': 'entity',
                                'typeRefId': 'supplierId',
                                'typeRefIdName': 'supplierName'
                            },
                            modalTitle: 'journal.form.line.expense.modal.title'
                        }
                    },
                    {
                        condition: { field: 'typeRefType', operator: 'notIn', value: ['customer', 'supplier'] },
                        set: { editable: false }
                    }
                ]
            }
        ],
        mobile: {
            left: {
                tag: { field: 'accountIdName' }
            },
            columns: [
                { id: 'description', label: 'journal.form.line.description' },
                { id: 'debitAmount', label: 'journal.form.line.debit', type: 'number', editable: true, decimals: 2 },
                { id: 'creditAmount', label: 'journal.form.line.credit', type: 'number', editable: true, decimals: 2 }
            ]
        },
        addLine: 'journal.fast.grid.add',
        tabAddsLine: true,
        // A paper clip on each row; the cursor's row is the one the side
        // panel's documents are for (lineDocumentsData).
        lineDocuments: 'journalLine',
        lineActions: {
            menuItems: [
                { action: 'delete', label: 'journal.fast.grid.delete', confirm: true, modalConfig: 'confirmDelete' }
            ]
        }
    },
    priceListProductGrid: {
        entity: 'priceListProduct',
        endpoint: 'priceList/:id/priceListProduct',
        parentIdField: 'priceListId',
        listHeader: { text: 'priceList.products.title' },
        columns: [
            { id: 'id', label: 'priceList.products.col.name', type: 'connect', width: '300px', editable: true, entity: 'product', displayField: 'name', valueMap: { id: 'id', name: 'name', sku: 'sku' }, modalTitle: 'priceList.products.modal.title', menuItemField: 'name' },
            { id: 'sku', label: 'priceList.products.col.sku', type: 'text', width: '300px', editable: false }
        ],
        mobile: {
            left: {
                tag: { field: 'name' }
            },
            columns: [
                { id: 'name', label: 'priceList.products.col.name' },
                { id: 'sku', label: 'priceList.products.col.sku' }
            ]
        },
        addLine: 'priceList.products.add',
        buttons: [
            { id: 'priceListProductGrid.save', action: 'save', label: 'all.grid.save', icon: 'save', variant: 'primary', invalidateQuery: 'priceList/:id/priceListItem' }
        ],
        lineActions: {
            menuItems: [
                { action: 'delete', label: 'common.delete', confirm: true, modalConfig: 'confirmDelete' }
            ]
        }
    },
    priceListProductGroupGrid: {
        entity: 'priceListProductGroup',
        endpoint: 'priceList/:id/priceListProductGroup',
        parentIdField: 'priceListId',
        listHeader: { text: 'priceList.productGroups.title' },
        columns: [
            { id: 'id', label: 'priceList.productGroups.col.name', type: 'connect', width: '300px', editable: true, entity: 'productGroup', displayField: 'id', valueMap: { id: 'id', name: 'id', description: 'description' }, modalTitle: 'priceList.productGroups.modal.title', menuItemField: 'id' },
            { id: 'description', label: 'priceList.productGroups.col.description', type: 'text', width: '300px', editable: false }
        ],
        mobile: {
            left: {
                tag: { field: 'id' }
            },
            columns: [
                { id: 'id', label: 'priceList.productGroups.col.name' },
                { id: 'description', label: 'priceList.productGroups.col.description' }
            ]
        },
        addLine: 'priceList.productGroups.add',
        buttons: [
            { id: 'priceListProductGroupGrid.save', action: 'save', label: 'all.grid.save', icon: 'save', variant: 'primary' }
        ],
        lineActions: {
            menuItems: [
                { action: 'delete', label: 'common.delete', confirm: true, modalConfig: 'confirmDelete' }
            ]
        }
    },
    productFamilyProductGrid: {
        entity: 'product',
        path: '/product/',
        endpoint: 'product',
        listHeader: {
            text: 'productFamily.products.list.title',
        },
        columns: [
            { id: 'sku', label: 'product.list.table.sku', type: 'text', width: '140px' },
            { id: 'name', label: 'product.list.table.name', type: 'text', width: '250px' },
            { id: 'type', label: 'product.list.table.type', type: 'text', width: '120px' },
        ],
        mobile: {
            left: {
                tag: { field: 'name' }
            },
            columns: [
                { id: 'name', label: 'product.list.table.name' },
                { id: 'sku', label: 'product.list.table.sku' },
                { id: 'type', label: 'product.list.table.type' },
            ]
        },
        filters: {
            list: [{
                label: 'productFamily.grid.filter.byFamily',
                applyFilter: true,
                terms: [{ id: 'familyId', value: '{id}' }],
            }],
        },
        orderBy: 'name',
        showFilterBar: false,
        reorderable: false,
        addLine: false
    },
    productFamilyVariantGrid: {
        entity: 'product',
        path: '/product/',
        endpoint: 'product',
        listHeader: {
            text: 'productFamily.products.list.title',
        },
        columns: [
            { id: 'sku', label: 'product.list.table.sku', type: 'text', width: '250px', editable: false },
            { id: 'name', label: 'product.list.table.name', type: 'text', width: '250px', editable: false },
            { id: 'netSalesPrice', label: 'product.list.table.netSalesPrice', type: 'number', width: '150px', editable: true, decimals: 5 },
            { id: 'grossSalesPrice', label: 'product.list.table.grossSalesPrice', type: 'number', width: '150px', editable: true, decimals: 5 },
        ],
        mobile: {
            left: {
                tag: { field: 'name' }
            },
            columns: [
                { id: 'name', label: 'product.list.table.name' },
                { id: 'netSalesPrice', label: 'product.list.table.netSalesPrice', type: 'number', editable: true, decimals: 5 },
                { id: 'grossSalesPrice', label: 'product.list.table.grossSalesPrice', type: 'number', editable: true, decimals: 5 },
            ]
        },
        filters: {
            list: [{
                label: 'productFamily.grid.filter.byFamily',
                applyFilter: true,
                terms: [{ id: 'familyId', value: '{id}' }],
            }],
        },
        orderBy: 'name',
        showFilterBar: false,
        reorderable: false,
        addLine: false,
        buttons: [
            { id: 'productFamilyVariantGrid.save', action: 'save', label: 'all.form.button.save', icon: 'save', variant: 'primary' },
        ]
    },
    productPriceGrid: {
        entity: 'itemPrice',
        endpoint: 'product/:id/itemPrice',
        listHeader: {
            text: 'product.price.grid.title',
        },
        columns: [
            { id: 'code', label: 'product.price.grid.code', type: 'select', width: '150px', editable: true, entity: 'priceType', displayField: 'codeName', valueMap: { code: 'id', codeName: 'name' } },
            { id: 'typeName', label: 'product.price.grid.type', type: 'text', width: '120px', editable: false },
            { id: 'value', label: 'product.price.grid.value', type: 'number', width: '100px', editable: true, decimals: 2 },
            { id: 'netSalesPrice', label: 'product.price.grid.price', type: 'amount', width: '120px', editable: false },
            { id: 'currency', label: 'product.price.grid.currency', type: 'text', width: '100px', editable: false },
        ],
        mobile: {
            left: {
                tag: { field: 'codeName' }
            },
            columns: [
                { id: 'codeName', label: 'product.price.grid.code' },
                { id: 'typeName', label: 'product.price.grid.type' },
                { id: 'value', label: 'product.price.grid.value', type: 'number', editable: true, decimals: 2 },
                { id: 'netSalesPrice', label: 'product.price.grid.price' },
                { id: 'currency', label: 'product.price.grid.currency' },
            ]
        },
        filters: { list: [], marginTop: '0rem' },
        orderBy: 'code',
        reorderable: false,
        addLine: 'product.price.grid.new',
        buttons: [
            { id: 'productPriceGrid.save', action: 'save', label: 'all.grid.save', icon: 'save', variant: 'primary' },
        ]
    },
    purchaseRequestLinesGrid: {
        entity: 'purchaseRequest',
        linesArray: 'lines',
        listHeader: { text: 'purchases.purchaseRequest.form.lines' },
        columns: [
            {
                id: 'itemId',
                label: 'purchases.purchaseRequest.form.line.product',
                type: 'connect',
                width: '320px',
                editable: true,
                entity: 'product',
                displayField: 'itemName',
                valueMap: {
                    itemId: 'id',
                    itemName: 'name',
                    itemDescription: 'description',
                    unitId: 'unitId',
                    unitName: 'unitName',
                    estimatedUnitPrice: 'unitCost'
                },
                options: { withHeaders: true }
            },
            {
                id: 'requestedQty',
                label: 'purchases.purchaseRequest.form.line.requestedQty',
                type: 'number',
                width: '100px',
                editable: true,
                decimals: 2
            },
            {
                id: 'unitId',
                label: 'purchases.purchaseRequest.form.line.unit',
                type: 'select',
                width: '120px',
                editable: true,
                entity: 'unit',
                displayField: 'unitName',
                valueMap: { unitId: 'id', unitName: 'name' }
            },
            {
                id: 'estimatedUnitPrice',
                label: 'purchases.purchaseRequest.form.line.estimatedUnitPrice',
                type: 'number',
                width: '120px',
                editable: true,
                decimals: 2
            },
            {
                id: 'note',
                label: 'purchases.purchaseRequest.form.line.note',
                type: 'text',
                width: '260px',
                editable: true
            }
        ],
        mobile: {
            left: {
                tag: { field: 'itemName' }
            },
            columns: [
                {id:'requestedQty', label:'purchases.purchaseRequest.form.line.quantity'},{id: 'unitName', label:'purchases.purchaseRequest.form.line.unit'}]
        },
        reorderable: true,
        addLine: 'purchases.purchaseRequest.form.lines.add',
        lineActions: {
            menuItems: [
                {
                    action: 'delete',
                    label: 'purchases.purchaseRequest.form.line.delete',
                    confirm: true
                }
            ]
        }
    },
    workOperationMaterialGrid: {
        entity: 'workOperation',
        parentEntity: 'workOperation',
        persistParent: true,
        linesArray: 'plannedInputs',
        listHeader: { text: 'workExecution.materials.title' },
        columns: [
            { id: 'productName', label: 'workExecution.materials.table.product', type: 'text', width: '260px', editable: false },
            { id: 'qty', label: 'workExecution.materials.table.required', type: 'quantity', width: '120px', editable: false },
            { id: 'actualQty', label: 'workExecution.materials.table.consumed', type: 'number', width: '120px', editable: true, decimals: 2 },
            { id: 'uomName', label: 'workExecution.materials.table.uom', type: 'text', width: '100px', editable: false },
        ],
        mobile: {
            left: {
                tag: { field: 'productName' }
            },
            columns: [
                { id: 'productName', label: 'workExecution.materials.table.product' },
                { id: 'qty', label: 'workExecution.materials.table.required' },
                { id: 'actualQty', label: 'workExecution.materials.table.consumed', type: 'number', editable: true, decimals: 2 },
                { id: 'uomName', label: 'workExecution.materials.table.uom' },
            ]
        },
        buttons: [
            { id: 'workOperationMaterialGrid.fillFromField', action: 'fillFromField', label: 'workExecution.materials.button.fillQty', variant: 'secondary', sourceField: 'qty', targetField: 'actualQty' },
            { id: 'workOperationMaterialGrid.save', action: 'save', label: 'all.grid.save', icon: 'save', variant: 'primary' },
        ]
    },
    workOperationOutputGrid: {
        entity: 'workOperation',
        linesArray: 'plannedOutputs',
        listHeader: { text: 'workExecution.outputs.title' },
        columns: [
            { id: 'productName', label: 'workExecution.outputs.table.product', type: 'text', width: '260px', editable: false },
            { id: 'qty', label: 'workExecution.outputs.table.plannedQty', type: 'quantity', width: '120px', editable: false },
            { id: 'uomName', label: 'workExecution.outputs.table.uom', type: 'text', width: '100px', editable: false },
        ],
        mobile: {
            left: {
                tag: { field: 'productName' }
            },
            columns: [
                { id: 'productName', label: 'workExecution.outputs.table.product' },
                { id: 'qty', label: 'workExecution.outputs.table.plannedQty' },
                { id: 'uomName', label: 'workExecution.outputs.table.uom' },
            ]
        }
    },
    workOperationRunsGrid: {
        entity: 'workOperation',
        linesArray: 'runEntries',
        listHeader: { text: 'workExecution.runs.title' },
        columns: [
            { id: 'type', label: 'workExecution.runs.table.type', type: 'text', width: '120px' },
            { id: 'startTime', label: 'workExecution.runs.table.startTime', type: 'datetime', width: '200px' },
            { id: 'endTime', label: 'workExecution.runs.table.endTime', type: 'datetime', width: '200px' },
        ],
        mobile: {
            left: {
                tag: { field: 'type' }
            },
            columns: [
                { id: 'type', label: 'workExecution.runs.table.type' },
                { id: 'startTime', label: 'workExecution.runs.table.startTime', type: 'datetime' },
                { id: 'endTime', label: 'workExecution.runs.table.endTime', type: 'datetime' },
            ]
        },
        readOnly: true
    },
}
